import type { IssueType, Project, Ticket } from '@servicetim/shared';
import { useCreateTicket, useIssueTypes, useProjects } from '@servicetim/ui/api';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Label,
  LoadingState,
  ProblemBanner,
  SchemaForm,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  toast,
  toCreateTicketInput,
} from '@servicetim/ui/components';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { useAgentUserOptionLoader } from './api';
import { CREATE_SYSTEM_FIELDS, useTicketFormSource } from './form-source';

export interface CreateTicketDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultProjectId?: string;
  /** Creates a child of this ticket (same project, sub-task types preferred). */
  parent?: { id: string; key: string; projectId: string };
  /** Defaults to navigating to the new ticket. */
  onCreated?: (ticket: Ticket) => void;
}

/**
 * "New ticket": project → issue type → the project's create form (`SchemaForm`), submitted
 * as `POST /v1/tickets`. Server `errors[].path` land on the fields.
 */
export function CreateTicketDialog(props: CreateTicketDialogProps) {
  const { open, onOpenChange, parent } = props;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>{parent ? `New child of ${parent.key}` : 'New ticket'}</DialogTitle>
          <DialogDescription>
            Pick the project and issue type; the form follows the project's configuration.
          </DialogDescription>
        </DialogHeader>
        {open ? <CreateTicketBody {...props} /> : null}
      </DialogContent>
    </Dialog>
  );
}

function preferredIssueType(types: readonly IssueType[], child: boolean): string | undefined {
  const preferred = types.find((t) => t.isSubtask === child) ?? types[0];
  return preferred?.id;
}

function CreateTicketBody({
  onOpenChange,
  defaultProjectId,
  parent,
  onCreated,
}: CreateTicketDialogProps) {
  const navigate = useNavigate();
  const projects = useProjects({ pageSize: 200 });
  const [projectId, setProjectId] = useState<string | undefined>(
    parent?.projectId ?? defaultProjectId,
  );
  const project: Project | undefined = projects.items.find((p) => p.id === projectId);
  const issueTypes = useIssueTypes(projectId);
  const [issueTypeId, setIssueTypeId] = useState<string | undefined>(undefined);
  useEffect(() => {
    if (!issueTypes.data) return;
    setIssueTypeId((current) =>
      current && issueTypes.data.some((t) => t.id === current)
        ? current
        : preferredIssueType(issueTypes.data, parent !== undefined),
    );
  }, [issueTypes.data, parent]);

  const source = useTicketFormSource({
    projectId,
    issueTypeId,
    kind: 'create',
    canAdmin: project?.canAdmin ?? false,
    systemFields: CREATE_SYSTEM_FIELDS,
    enabled: projectId !== undefined && issueTypeId !== undefined,
  });
  const loadUsers = useAgentUserOptionLoader();
  const pickers = useMemo(() => ({ users: loadUsers }), [loadUsers]);
  const create = useCreateTicket();

  const submit = async (values: Record<string, unknown>) => {
    if (!projectId || !issueTypeId) return;
    const input = toCreateTicketInput(values, { projectId, issueTypeId });
    const ticket = await create.mutateAsync({
      ...input,
      ...(parent ? { parentId: parent.id } : {}),
    });
    toast.success(`Created ${ticket.key}`);
    onOpenChange(false);
    if (onCreated) onCreated(ticket);
    else void navigate({ to: '/tickets/$idOrKey', params: { idOrKey: ticket.key } });
  };

  return (
    <div className="space-y-5">
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="create-ticket-project">Project</Label>
          <Select
            value={projectId ?? ''}
            onValueChange={(next) => {
              setProjectId(next);
              setIssueTypeId(undefined);
            }}
            disabled={parent !== undefined || projects.isPending}
          >
            <SelectTrigger id="create-ticket-project">
              <SelectValue placeholder={projects.isPending ? 'Loading…' : 'Select a project'} />
            </SelectTrigger>
            <SelectContent>
              {projects.items
                .filter((p) => p.archivedAt === null)
                .map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.key} · {p.name}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="create-ticket-type">Issue type</Label>
          <Select
            value={issueTypeId ?? ''}
            onValueChange={setIssueTypeId}
            disabled={projectId === undefined || issueTypes.isPending}
          >
            <SelectTrigger id="create-ticket-type">
              <SelectValue
                placeholder={
                  projectId === undefined
                    ? 'Pick a project first'
                    : issueTypes.isPending
                      ? 'Loading…'
                      : 'Select an issue type'
                }
              />
            </SelectTrigger>
            <SelectContent>
              {(issueTypes.data ?? []).map((t) => (
                <SelectItem key={t.id} value={t.id}>
                  {t.name}
                  {t.isSubtask ? ' (sub-task)' : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      {projects.error ? <ProblemBanner error={projects.error} size="sm" /> : null}
      {issueTypes.error ? <ProblemBanner error={issueTypes.error} size="sm" /> : null}
      {source.error ? <ProblemBanner error={source.error} size="sm" /> : null}
      {source.isPending ? <LoadingState rows={4} label="Loading the form…" /> : null}
      {source.sections && projectId && issueTypeId ? (
        <SchemaForm
          key={`${projectId}:${issueTypeId}:${source.source}`}
          sections={source.sections}
          defaultValues={source.defaults}
          onSubmit={submit}
          submitLabel="Create"
          onCancel={() => onOpenChange(false)}
          busy={create.isPending}
          pickers={pickers}
          emptyValue="omit"
        />
      ) : null}
    </div>
  );
}
